<template>
  <section class="support-requests-section">
    <div class="header-container">
      <h1 class="section-title">Support Requests</h1>
      
      <!-- Tab navigation -->
      <div class="tabs-container">
        <button 
          class="tab-button" 
          :class="{'active': activeTab === 'active'}" 
          @click="changeTab('active')"
        >
          Active Requests
        </button>
        <button 
          class="tab-button" 
          :class="{'active': activeTab === 'archived'}" 
          @click="changeTab('archived')"
        >
          Archived Requests
        </button>
      </div>
    </div>

    <!-- Search and filter options -->
    <div class="filters-container">
      <div class="search-container">
        <div class="search-input-wrapper">
          <div class="search-icon">
            <svg aria-hidden="true" class="w-5 h-5 text-gray-500" fill="currentColor"
                viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clip-rule="evenodd"></path>
            </svg>
          </div>
          <input 
            type="text" 
            class="search-input"
            placeholder="Search by name or description" 
            v-model="searchData.keyword" 
            @input="debounceSearch"
          >
        </div>
      </div>
      
      <div class="sort-container">
        <select 
          class="sort-select" 
          v-model="searchData.sortBy" 
          @change="updateSort"
        >
          <option value="">Sort By</option>
          <option value="created_at">Date</option>
          <option value="first_name">Name</option>
          <option value="response">Response Status</option>
        </select>
        
        <select 
          class="sort-select" 
          v-model="searchData.sortOrder" 
          @change="updateSort"
          :disabled="!searchData.sortBy"
        >
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </select>
      </div>
    </div>

    <!-- Content area -->
    <div class="content-container">
      <div class="table-container">
        <table class="support-table">
          <thead>
            <tr>
              <th class="th-first">Name</th>
              <th>Description</th>
              <th>Date</th>
              <th>Response</th>
              <th class="th-last">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="questionListLength" v-for="r in questionList.data" v-bind:key="r.id" class="table-row">
              <td class="name-cell">
                {{ r.first_name }} {{ r.last_name }}
              </td>
              <td class="description-cell">
                <p>{{ r.description | truncate(50) }}</p>
              </td>
              <td>
                {{ r.created_at | timeAgo }}
              </td>
              <td>
                <div class="response-status" :class="{'responded': r.response}">
                  <span v-if="r.response">Responded</span>
                  <span v-else>Pending</span>
                </div>
              </td>
              <td class="action-cell">
                <div class="action-buttons">
                  <!-- Forward to Admin button -->
                  <button
                    v-if="!r.forward_to_admin && activeTab === 'active' && (user.role == 'ADMIN' || (company && company.role == 'COMPANY_ADMIN'))"
                    class="forward-btn action-btn"
                    @click="forwardToAdmin(r.id)"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>Forward</span>
                  </button>

                  <!-- Forwarded status -->
                  <div
                    v-if="r.forward_to_admin && (user.role == 'ADMIN' || (company && company.role == 'COMPANY_ADMIN'))"
                    class="forwarded-status"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>Forwarded</span>
                  </div>

                  <!-- Respond button -->
                  <button
                    v-if="activeTab === 'active' && ((user.role == 'ADMIN' && !r.response) || (company && company.role == 'COMPANY_ADMIN' && !r.response && r.company_id != company.id))"
                    class="respond-btn action-btn"
                    v-b-modal.response-modal
                    @click="respondQuestion(r.id)"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>Respond</span>
                  </button>

                  <!-- Action buttons based on tab -->
                  <template v-if="activeTab === 'active'">
                    <!-- Archive button for active tab -->
                    <button
                      class="archive-btn action-btn"
                      @click="archiveQuestion(r.id)"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 8v13H3V8M1 3h22v5H1V3zM10 12h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <span>Archive</span>
                    </button>
                  </template>
                  
                  <template v-else>
                    <!-- Restore button for archived tab -->
                    <button
                      class="restore-btn action-btn"
                      @click="restoreQuestion(r.id)"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 12h1m8-9v1m8 8h1M5.6 5.6l.7.7m12.1-.7l-.7.7M9 16a5 5 0 116 0 3.5 3.5 0 00-1 3 2 2 0 01-4 0 3.5 3.5 0 00-1-3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <span>Restore</span>
                    </button>
                  </template>

                  <!-- View details button (always shown) -->
                  <router-link :to="'view-question/' + r.id" class="view-btn action-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>View</span>
                  </router-link>
                </div>
              </td>
            </tr>
            <tr v-if="!questionListLength">
              <td colspan="5" class="no-data">
                <div class="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p v-if="activeTab === 'active'">No active support requests found</p>
                  <p v-else>No archived support requests found</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination-wrapper" v-if="questionListLength">
          <custom-pagination 
            :data="questionList" 
            @pagination-change-page="getQuestionList" 
            :limit="2"
          />
        </div>
      </div>
    </div>

    <!-- Response Modal -->
    <b-modal id="response-modal" size="lg" title="Respond to Question" :hide-footer="hideFooter" no-enforce-focus>
      <div class="modal-form">
        <div class="form-group">
          <label>Response <span class="required">*</span></label>
          <vue2-tinymce-editor 
            v-model="response.response" 
            placeholder="Type your response here..."
            :init="{
              height: 300,
              menubar: false,
              plugins: [
                'advlist autolink lists link image charmap print preview anchor',
                'searchreplace visualblocks code fullscreen',
                'insertdatetime media table paste code help wordcount'
              ],
              toolbar:
                'undo redo | formatselect | bold italic backcolor | \
                alignleft aligncenter alignright alignjustify | \
                bullist numlist outdent indent | removeformat | help'
            }"
          ></vue2-tinymce-editor>
        </div>
        <div class="form-actions">
          <button 
            type="button" 
            class="cancel-btn" 
            @click="$bvModal.hide('response-modal')"
          >
            Cancel
          </button>
          <button 
            type="button" 
            class="submit-btn" 
            @click="submitResponse" 
            :disabled="response.disabled"
          >
            <span v-if="response.disabled">Submitting...</span>
            <span v-else>Submit Response</span>
          </button>
        </div>
      </div>
    </b-modal>

  </section>
</template>

<script>
/* eslint-disable */
import AppMixin from '../../../mixins/AppMixin'
import { Vue2TinymceEditor } from "vue2-tinymce-editor";
import Api from '../../../router/api'
import CustomPagination from '../CustomPagination.vue'

export default {
  name: 'SupportRequests',
  components: {
    Vue2TinymceEditor,
    CustomPagination
  },
  mixins: [AppMixin],
  data() {
    return {
      hideFooter: true,
      msg: 'Support Requests',
      questionList: {},
      questionListLength: 0,
      searchData: {
        'keyword': '',
        'sortBy': 'created_at',
        'sortOrder': 'desc',
        'status': 'active' // 'active' or 'archived'
      },
      response: {
        questionId: '',
        response: '',
        disabled: false
      },
      type: "",
      activeTab: 'active',
      searchTimeout: null
    }
  },
  methods: {
    changeTab(tab) {
      this.activeTab = tab;
      this.searchData.status = tab;
      this.getQuestionList();
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.getQuestionList();
      }, 500);
    },
    
    updateSort() {
      this.getQuestionList();
    },
    
    respondQuestion: function (id) {
      this.response.questionId = id;
      this.response.response = '';
    },
    
    submitResponse: function () {
      if (!this.response.response) {
        this.$swal({
          icon: "error",
          title: "Error",
          text: "Please enter a response",
          showConfirmButton: true
        });
        return;
      }
      
      this.response.disabled = true;
      
      Api.submitResponse(this.response).then(response => {
        this.$swal({
          icon: "success",
          title: "Success",
          text: "Response submitted successfully",
          showConfirmButton: true
        }).then(() => {
          this.$bvModal.hide('response-modal');
          this.response.response = '';
          this.response.disabled = false;
          this.getQuestionList();
        });
      }).catch((error) => {
        this.response.disabled = false;
        this.$swal({
          icon: "error",
          title: "Error",
          text: error.response && error.response.data && error.response.data.message ? error.response.data.message : "Something went wrong",
          showConfirmButton: true
        });
      });
    },
    
    getQuestionList: function (page = 1) {
      Api.getQuestionList(page, this.searchData).then(response => {
        this.questionList = response.data.res;
        this.questionListLength = this.questionList.data.length;
      }).catch((error) => {
        this.$swal({
          icon: "error",
          title: "Error",
          text: error.response && error.response.data && error.response.data.message ? error.response.data.message : "Failed to load support requests",
          showConfirmButton: true
        });
      });
    },
    
    forwardToAdmin: function (id) {
      this.$swal({
        title: 'Forward to Admin',
        text: 'Are you sure you want to forward this request to the super admin?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Forward It',
        cancelButtonText: 'Cancel',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          Api.forwardToAdmin(id).then(response => {
            this.$swal({
              icon: "success",
              title: "Success",
              text: "Request forwarded to admin successfully",
              showConfirmButton: true
            }).then(() => {
              this.getQuestionList();
            });
          }).catch((error) => {
            this.$swal({
              icon: "error",
              title: "Error",
              text: error.response && error.response.data && error.response.data.message ? error.response.data.message : "Failed to forward request",
              showConfirmButton: true
            });
          });
        }
      });
    },
    
    archiveQuestion: function (id) {
      this.$swal({
        title: 'Archive Request',
        text: 'Are you sure you want to archive this support request?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Archive It',
        cancelButtonText: 'Cancel',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          Api.archiveQuestion(id).then(response => {
            this.$swal({
              icon: "success",
              title: "Success",
              text: "Support request archived successfully",
              showConfirmButton: true
            }).then(() => {
              this.getQuestionList();
            });
          }).catch((error) => {
            this.$swal({
              icon: "error",
              title: "Error",
              text: error.response && error.response.data && error.response.data.message ? error.response.data.message : "Failed to archive request",
              showConfirmButton: true
            });
          });
        }
      });
    },
    
    restoreQuestion: function (id) {
      this.$swal({
        title: 'Restore Request',
        text: 'Are you sure you want to restore this archived request?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Restore It',
        cancelButtonText: 'Cancel',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          Api.restoreQuestion(id).then(response => {
            this.$swal({
              icon: "success",
              title: "Success",
              text: "Support request restored successfully",
              showConfirmButton: true
            }).then(() => {
              this.getQuestionList();
            });
          }).catch((error) => {
            this.$swal({
              icon: "error",
              title: "Error",
              text: error.response && error.response.data && error.response.data.message ? error.response.data.message : "Failed to restore request",
              showConfirmButton: true
            });
          });
        }
      });
    }
  },
  mounted() {
    this.getQuestionList();
  },
  filters: {
    truncate(text, length) {
      if (text && text.length > length) {
        return text.substring(0, length) + '...';
      }
      return text;
    }
  }
}
</script>

<style scoped>
.support-requests-section {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding-bottom: 2rem;
}

.header-container {
  background-color: white;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .header-container {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .tabs-container {
    margin-top: 1rem;
    width: 100%;
  }
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #090446;
  margin: 0;
}

.tabs-container {
  display: flex;
  gap: 0.5rem;
}

.tab-button {
  padding: 0.5rem 1rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background-color: #f8f9fa;
  color: #6c757d;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-button:hover {
  background-color: #e9ecef;
}

.tab-button.active {
  background-color: #090446;
  color: white;
  border-color: #090446;
}

.filters-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  flex-wrap: wrap;
  gap: 1rem;
}

@media (max-width: 768px) {
  .filters-container {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-container {
    width: 100%;
  }
  
  .sort-container {
    width: 100%;
    display: flex;
    gap: 1rem;
  }
  
  .sort-select {
    flex: 1;
  }
}

.search-container {
  flex: 1;
  max-width: 450px;
}

.search-input-wrapper {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #212529;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.search-input:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.sort-container {
  display: flex;
  gap: 0.5rem;
}

.sort-select {
  padding: 0.5rem 2rem 0.5rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 8px;
  background-color: white;
  color: #212529;
  font-size: 0.95rem;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 16px 12px;
}

.sort-select:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.content-container {
  padding: 0 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.table-container {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.support-table {
  width: 100%;
  border-collapse: collapse;
}

.support-table th,
.support-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.support-table th {
  background-color: #090446;
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.th-first {
  border-top-left-radius: 8px;
}

.th-last {
  border-top-right-radius: 8px;
}

.table-row:hover {
  background-color: #f8f9fa;
}

.name-cell {
  font-weight: 600;
  color: #090446;
}

.description-cell {
  max-width: 300px;
}

.response-status {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: #ffeaed;
  color: #dc3545;
}

.response-status.responded {
  background-color: #e6f5ea;
  color: #198754;
}

.action-cell {
  width: 300px;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.15s ease;
  white-space: nowrap;
  text-decoration: none;
}

.forward-btn {
  background-color: #fff;
  color: #6c757d;
  border-color: #ced4da;
}

.forward-btn:hover {
  background-color: #f8f9fa;
}

.respond-btn {
  background-color: #fff;
  color: #0d6efd;
  border-color: #ced4da;
}

.respond-btn:hover {
  background-color: #f0f7ff;
}

.archive-btn {
  background-color: #fff;
  color: #dc3545;
  border-color: #ced4da;
}

.archive-btn:hover {
  background-color: #fff5f5;
}

.restore-btn {
  background-color: #fff;
  color: #198754;
  border-color: #ced4da;
}

.restore-btn:hover {
  background-color: #f0fff4;
}

.view-btn {
  background-color: #fff;
  color: #090446;
  border-color: #ced4da;
}

.view-btn:hover {
  background-color: #f0f0f7;
}

.forwarded-status {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #198754;
  background-color: #e6f5ea;
}

.no-data {
  text-align: center;
  padding: 3rem !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #6c757d;
}

.empty-state svg {
  color: #ced4da;
  width: 48px;
  height: 48px;
}

.pagination-wrapper {
  padding: 1.5rem;
  display: flex;
  justify-content: center;
}

/* Modal styles */
.modal-form {
  padding: 1rem 0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #212529;
}

.required {
  color: #dc3545;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.cancel-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: white;
  color: #6c757d;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cancel-btn:hover {
  background-color: #f8f9fa;
}

.submit-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  background-color: #090446;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  background-color: #060229;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 992px) {
  .action-buttons {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .action-btn {
    width: fit-content;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/common/customer-support/AskYourCareTeam.vue